// Choosing bots before the draft (AI_MANAGERS.md §3.1, scenario B7, B-459):
// one seat by character and band, or every open seat at once round a band.
//
// THE CAST COMES FROM THE SERVER (`GET /api/bots`), so a second client
// chooses from the same characters in the same words. Nothing here decides
// who plays well: the bands' labels and promises are the server's too.
//
// A BOT SEAT STAYS A FRIEND'S, and both sheets say so before anything is
// seated: the league's code takes an empty seat first, then a bot's.

import { useState } from 'react';
import type {
  BotCastMember,
  BotCastView,
  BotDials,
  LeagueSetupView,
  SkillBandId,
} from '@fsp/domain';
import { botFace } from '@fsp/domain';
import { messageOf } from '../../data/api';
import { fillSeats, seatBot, useBotCast } from '../../data/leagueSetup';
import { Sheet } from '../../kit/overlays';
import { FaceArt } from '../../kit/FaceArt';
import '../../styles/screens/predraft.css';
import {
  Button,
  Callout,
  Choice,
  Line,
  ListRow,
  OneOf,
  RowName,
  RowSub,
  Stack,
  Text,
  tell,
} from '../../kit/ui';

const reached = 'Could not reach the site. Try again.';

/**
 * A character's face as its seat will wear it (B-464), on a plain disc: the
 * seat's own colors are the seat's, and a face is chosen before it has one.
 */
function BotChip({ id }: { id: string }) {
  const face = botFace(id, new Date());
  return face === null ? null : (
    <span className="fsp-bot-chip" aria-hidden>
      <FaceArt face={face} />
    </span>
  );
}

/** A dial's three stops, as the chooser offers them, and the value each stores. */
type Stop = 'low' | 'mid' | 'high';
const STOP_VALUE: Record<Stop, number> = { low: 0.15, mid: 0.5, high: 0.9 };
// THE SERVER'S LINES: the chooser's *talks a lot* is above 0.7 (`LOUD_TALK`),
// so a character's row and its Adjust never disagree (the review of B-462).
const stopOf = (v: number): Stop => (v < 0.34 ? 'low' : v > 0.7 ? 'high' : 'mid');
const EVEN: Record<keyof BotDials, Stop> = {
  trashTalk: 'mid',
  aggression: 'mid',
  risk: 'mid',
  loyalty: 'mid',
  activity: 'mid',
};
const stopsOf = (dials: BotDials): Record<keyof BotDials, Stop> => ({
  trashTalk: stopOf(dials.trashTalk),
  aggression: stopOf(dials.aggression),
  risk: stopOf(dials.risk),
  loyalty: stopOf(dials.loyalty),
  activity: stopOf(dials.activity),
});
/** Only the dials moved off the character's own stop; nothing moved is nothing sent. */
const changedDials = (own: BotDials, stops: Record<keyof BotDials, Stop>): Partial<BotDials> =>
  Object.fromEntries(
    (Object.keys(stops) as (keyof BotDials)[])
      .filter((k) => stops[k] !== stopOf(own[k]))
      .map((k) => [k, STOP_VALUE[stops[k]]]),
  );
/** The five dials in a person's words (AI_MANAGERS.md §4.4), low, middle, high. */
const DIALS: { key: keyof BotDials; label: string; words: [string, string, string] }[] = [
  { key: 'trashTalk', label: 'How much it talks', words: ['Rarely', 'Sometimes', 'A lot'] },
  {
    key: 'activity',
    label: 'How often it looks at its team',
    words: ['About once a week', 'Every other day', 'Nearly every day'],
  },
  {
    key: 'aggression',
    label: 'How often it trades and bids',
    words: ['Rarely', 'Sometimes', 'Often'],
  },
  {
    key: 'risk',
    label: 'How it treats a player on the injury report',
    words: ['Careful', 'Middling', 'Bold'],
  },
  {
    key: 'loyalty',
    label: 'How much it holds on to its own picks',
    words: ['A little', 'Fairly', 'Fiercely'],
  },
];
const CLAIMABLE =
  'A friend who joins with the league’s code still gets a seat: an empty one first, then a bot’s.';
/** Past the draft the code seats nobody: a team changes hands by its own link (J4). */
const CLAIMABLE_DRAFTED =
  'The team keeps its name. Its own link still hands it to a friend, who takes it over from the bot.';

/** The band choice: a band, or (for one seat) the character's own. */
function BandChoice({
  cast,
  value,
  onChange,
  own,
}: {
  cast: BotCastView;
  value: SkillBandId | 'own';
  onChange: (band: SkillBandId | 'own') => void;
  /** Offer "Each its own" for choosing one character. */
  own: boolean;
}) {
  const promise =
    value === 'own'
      ? 'Each plays at its own level, as its card says.'
      : (cast.bands.find((b) => b.id === value)?.promise ?? '');
  return (
    <Choice
      label="How well they play"
      description={promise}
      searchable={false}
      required
      options={[
        ...(own ? [{ value: 'own', label: 'Its own level' }] : []),
        ...cast.bands.map((b) => ({ value: b.id, label: b.label })),
      ]}
      value={value}
      onChange={(v) => {
        if (v !== null) onChange(v as SkillBandId | 'own');
      }}
    />
  );
}

/** One seat: a character from the cast, or the house's pick. */
export function BotSeatSheet({
  leagueId,
  teamId,
  onClose,
  onView,
  drafted = false,
}: {
  leagueId: string;
  /** The seat being filled; null is closed. */
  teamId: string | null;
  onClose: () => void;
  onView: (view: LeagueSetupView) => void;
  /** Past the draft (Managers, J3): the code seats nobody, and the team keeps its name. */
  drafted?: boolean;
}) {
  const read = useBotCast(teamId !== null).snapshot;
  const cast = read.state === 'ready' ? read.value : null;
  const [band, setBand] = useState<SkillBandId | 'own'>('own');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const close = () => {
    setProblem(null);
    onClose();
  };
  // A CHARACTER'S DIALS, ADJUSTED (B-462): which one is open, and its stops.
  const [adjusting, setAdjusting] = useState<string | null>(null);
  const [stops, setStops] = useState<Record<keyof BotDials, Stop>>(EVEN);
  const adjust = (c: BotCastMember) => {
    if (adjusting === c.id) return setAdjusting(null);
    setStops(stopsOf(c.dials));
    setAdjusting(c.id);
  };
  const seat = async (presetId: string | undefined, name: string, dials?: Partial<BotDials>) => {
    if (teamId === null) return;
    setBusy(true);
    setProblem(null);
    const reply = await seatBot(leagueId, teamId, {
      ...(presetId === undefined ? {} : { presetId }),
      ...(band === 'own' ? {} : { band }),
      ...(dials === undefined || Object.keys(dials).length === 0 ? {} : { dials }),
    });
    setBusy(false);
    if (reply.status !== 'ok') return setProblem(messageOf(reply, 'That did not work.', reached));
    onView(reply.data);
    tell(`${name} takes the seat.`, 'good');
    close();
  };
  return (
    <Sheet
      opened={teamId !== null}
      onClose={close}
      title={drafted ? 'Choose a bot for this team' : 'Put a bot in this seat'}
      wide
    >
      <Stack gap={3}>
        <Text size="sm" tone="secondary" block>
          {drafted ? CLAIMABLE_DRAFTED : CLAIMABLE}
        </Text>
        {cast === null ? (
          <Text tone="secondary">
            {read.state === 'failed' ? read.failure.message : 'Loading the bots…'}
          </Text>
        ) : (
          <>
            <BandChoice cast={cast} value={band} onChange={setBand} own />
            <Button
              variant="secondary"
              loading={busy}
              onClick={() => void seat(undefined, 'A bot')}
            >
              Any bot
            </Button>
            <div>
              {cast.cast.map((c) => (
                <div key={c.id}>
                  <ListRow
                    tall
                    lead={<BotChip id={c.id} />}
                    trail={
                      <Line gap={1}>
                        <Button
                          size="xs"
                          variant="quiet"
                          expanded={adjusting === c.id}
                          onClick={() => adjust(c)}
                        >
                          Adjust
                        </Button>
                        <Button size="xs" loading={busy} onClick={() => void seat(c.id, c.name)}>
                          Choose
                        </Button>
                      </Line>
                    }
                  >
                    <RowName>{c.name}</RowName>
                    <RowSub wrap>
                      “{c.voice}” {c.plays} Talks {c.talks}
                      {band === 'own'
                        ? ` · ${cast.bands.find((b) => b.id === c.band)?.label ?? ''}`
                        : ''}
                    </RowSub>
                    {/* HOW IT TRADES (B-182), at the band it would be seated
                        at: the server's words, so a manager can learn which
                        bot to deal with. */}
                    <RowSub wrap>In a trade it {c.trades[band === 'own' ? c.band : band]}.</RowSub>
                  </ListRow>
                  {/* ITS PERSONALITY, THE COMMISSIONER'S WAY (B-462): five
                      dials, each in three plain stops, set from its own. */}
                  {adjusting === c.id ? (
                    <div className="fsp-bot-dials">
                      <Stack gap={2}>
                        {DIALS.map((d) => (
                          <OneOf
                            key={d.key}
                            label={d.label}
                            options={(['low', 'mid', 'high'] as const).map((s, i) => ({
                              value: s,
                              label: d.words[i]!,
                            }))}
                            value={stops[d.key]}
                            onChange={(s) => setStops((was) => ({ ...was, [d.key]: s }))}
                          />
                        ))}
                        <Button
                          loading={busy}
                          onClick={() => void seat(c.id, c.name, changedDials(c.dials, stops))}
                        >
                          {`Choose ${c.name} this way`}
                        </Button>
                      </Stack>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </>
        )}
        {problem !== null ? <Callout tone="warn">{problem}</Callout> : null}
      </Stack>
    </Sheet>
  );
}

/** Every open seat at once, round one band. */
export function FillSeatsSheet({
  leagueId,
  open,
  opened,
  onClose,
  onView,
}: {
  leagueId: string;
  /** How many seats are open to fill. */
  open: number;
  opened: boolean;
  onClose: () => void;
  onView: (view: LeagueSetupView) => void;
}) {
  const read = useBotCast(opened).snapshot;
  const cast = read.state === 'ready' ? read.value : null;
  const [band, setBand] = useState<SkillBandId>('solid');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const close = () => {
    setProblem(null);
    onClose();
  };
  const fill = async () => {
    setBusy(true);
    setProblem(null);
    const reply = await fillSeats(leagueId, band);
    setBusy(false);
    if (reply.status !== 'ok') return setProblem(messageOf(reply, 'That did not work.', reached));
    onView(reply.data);
    tell(
      open === 1 ? 'A bot takes the open seat.' : `Bots take the ${String(open)} open seats.`,
      'good',
    );
    close();
  };
  const label = cast?.bands.find((b) => b.id === band)?.label ?? '';
  return (
    <Sheet opened={opened} onClose={close} title="Fill the open seats with bots">
      <Stack gap={3}>
        {cast === null ? (
          <Text tone="secondary">
            {read.state === 'failed' ? read.failure.message : 'Loading the bots…'}
          </Text>
        ) : (
          <>
            <BandChoice
              cast={cast}
              value={band}
              onChange={(b) => {
                if (b !== 'own') setBand(b);
              }}
              own={false}
            />
            <Text size="sm" block>
              {open === 1
                ? `The open seat gets a bot playing at ${label}.`
                : `The ${String(open)} open seats get a shuffle of characters playing around ${label}, not all at one level, and no more than three of the league’s loud ones while a quieter one is left.`}
            </Text>
            <Text size="sm" tone="secondary" block>
              {CLAIMABLE}
            </Text>
            <Button loading={busy} onClick={() => void fill()}>
              {open === 1 ? 'Fill the seat' : `Fill ${String(open)} seats`}
            </Button>
          </>
        )}
        {problem !== null ? <Callout tone="warn">{problem}</Callout> : null}
      </Stack>
    </Sheet>
  );
}
